import { Button, Grid, Paper, Table, TableBody, TableContainer, TableHead, TablePagination, TableRow} from "@mui/material";
import React, { useState } from "react";
import PropTypes from 'prop-types'
import AlternateRow from "../../../components/AlternateRow";
import SubmittedCell from "../../../components/Submitted/SubmittedCell";
import moment from "moment";
import { removeOfferDraft } from "../../../util/offerLettersHelp";
import { downloadDocusign } from "../../../util/docusignHelper";
/**
 * component used to view sent offer letters
 * @component 
 * @category Offer Letters 
 */

const defaultPagination = {
    total_items:0,
    limit:25,
    current_page:0
}

function ViewOffers({offers, updateOffers, onError, unvoidableStatuses, pagination=defaultPagination, loading, onPageinate, onLoadOffer, loadingOfferId}){
    const [loadingIndices, setLoadingIndices] = useState([])

    const handleChangePage = (event, newPage) => {
        onPageinate(newPage+1,pagination.limit)
    };
    
    const handleChangeRowsPerPage = (event, newValue) => {
        const newLimit = Number(event.target.value)
        //ensure page isn't too big
        onPageinate(Math.min(pagination.current_page, Math.floor((pagination.total_items+1) / newLimit)), newLimit)
    };

    const updateLoadingIndices = (id, method="append") => {
        let newIndices = [...loadingIndices]
        if(method === 'append'){
            newIndices.push(id)
        }else if(method === 'delete' && loadingIndices.includes(id)){
            newIndices.splice(newIndices.indexOf(id))
        }
        setLoadingIndices(newIndices)
    }

    const voidOfferLetter = (id) => {
        updateLoadingIndices(id)
        
        removeOfferDraft(id, (resp)=>{
            if(resp){
                if(updateOffers){
                    updateOffers()
                }
            }else{
                if(onError){
                    onError()
                }
            }
        },false)
    }


    const downloadOfferLetter = (id, env_id) => {
        updateLoadingIndices(id)
        downloadDocusign(env_id, (blob)=>{
            if(!blob){
                onError({errorTitle: "Uh Oh!", errorContent:"Something went wrong when trying to download the document, please try again"})
            }else{
                var url  = window.URL.createObjectURL(blob);
                window.open(url);
            }
            updateLoadingIndices(id,'delete')
        })
    }


    return(
        <Grid container sx={{padding:'2%'}}>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <SubmittedCell>Candidate Name</SubmittedCell>
                            <SubmittedCell>Candidate Email</SubmittedCell>
                            <SubmittedCell>Candidate Region</SubmittedCell>
                            <SubmittedCell>Date Sent (YYYY-MM-DD)</SubmittedCell>
                            <SubmittedCell>Signing Authority</SubmittedCell>
                            <SubmittedCell>Status</SubmittedCell>
                            <SubmittedCell/>
                            <SubmittedCell/>
                            <SubmittedCell/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            offers && offers.length > 0 ? (
                                <>
                                {offers.map((offer)=>(
                                    <AlternateRow>
                                        <SubmittedCell loading={loading}>
                                            {offer.name}
                                        </SubmittedCell>
                                        <SubmittedCell loading={loading}>
                                            {offer.email_address}
                                        </SubmittedCell>
                                        <SubmittedCell loading={loading}>
                                            {offer.region}
                                        </SubmittedCell>
                                        <SubmittedCell loading={loading}>
                                            {moment.utc(offer.date_submitted).format("YYYY-MM-DD").toString()}
                                        </SubmittedCell>
                                        <SubmittedCell loading={loading}>
                                            {offer.authority_name}
                                        </SubmittedCell>
                                        <SubmittedCell loading={loading}>
                                            {offer.status}
                                        </SubmittedCell>
                                        <SubmittedCell loading={loading}>
                                            <Button disabled={(unvoidableStatuses && unvoidableStatuses.includes(offer.status)) || offer.voided
                                            || loadingIndices.includes(offer.id)} 
                                                onClick={(e)=>{e.preventDefault(); voidOfferLetter(offer.id)}}>
                                                    {loadingIndices.includes(offer.id) ? "Please Wait" : "Void Offer"}
                                            </Button>
                                        </SubmittedCell>
                                        <SubmittedCell loading={loading}>
                                            <Button disabled={offer.status !== 'completed' || loadingIndices.includes(offer.id)} 
                                                onClick={(e)=>{e.preventDefault(); downloadOfferLetter(offer.id,offer.envelope_id)}}>
                                                    {loadingIndices.includes(offer.id) ? "Please Wait" : "Download Offer"}
                                            </Button>
                                        </SubmittedCell>
                                        <SubmittedCell loading={loading}>
                                            <Button disabled={!onLoadOffer || Boolean(loadingOfferId)}
                                                onClick={(e)=>{e.preventDefault(); onLoadOffer(offer)}}>
                                                    {loadingOfferId === offer.id ? "Please Wait" : "Load Offer"}
                                            </Button>
                                        </SubmittedCell>
                                    </AlternateRow>
                                ))}
                                </>
                            ):<></>
                        }
                    </TableBody>
                </Table>
                <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={pagination.total_items}
                rowsPerPage={pagination.limit || 25}
                page={pagination.current_page-1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </Grid>
    )   
}

ViewOffers.propTypes ={
    /**
     * list of offer letter objects following db schema
     */
    offers : PropTypes.arrayOf(PropTypes.object).isRequired,
    /**
     * function called when an error occures
     */
    onError: PropTypes.func,
    /**
     * function called to update offers
     */
    updateOffers : PropTypes.func,
    /**
     * statuses that aren't allowed to voided
     */
    unvoidableStatuses : PropTypes.arrayOf(PropTypes.string),
    /**
     * function called with an offer letter object to fill the offer form with it
     */
    onLoadOffer : PropTypes.func,
    /**
     * id of the offer letter currently being loaded, if any
     */
    loadingOfferId : PropTypes.number,
    /**
     * keys in the offer letter object that can be searched by
     */
    searchKeys: PropTypes.arrayOf(PropTypes.string)
}

ViewOffers.defaultProps = {
    searchKeys : ['name', 'email_address', 'region', 'date_submitted', 'authority_name', 'status'],
    unvoidableStatuses: ['voided', 'deleted', 'completed']
}

export default ViewOffers